/** /auth/login — step 1 of the 2-step login flow. */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Container,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { isMfaPending } from '@/api/auth';
import { ConsoleApiError } from '@/api/client';
import { BrandLogo } from '@/layout/BrandLogo';

import { useAuth } from './AuthProvider';
import { resolveLoginDestination, withReturnTo } from './returnTo';

const LoginSchema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});
type LoginInput = z.infer<typeof LoginSchema>;

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(LoginSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const r = await auth.login(values.email, values.password);
      const dest = resolveLoginDestination(searchParams, location.state);
      if (isMfaPending(r)) {
        navigate('/auth/mfa', {
          replace: true,
          state: { pending: r, from: location.state?.from ?? null, returnTo: dest },
        });
        return;
      }
      if (r.must_enroll_mfa) {
        // Enrolment is forced first; the destination rides along so it is not lost.
        navigate(withReturnTo('/auth/mfa/enroll', dest), { replace: true });
        return;
      }
      navigate(dest, { replace: true });
    } catch (e) {
      if (e instanceof ConsoleApiError) {
        setServerError(e.message);
      } else {
        setServerError('Unexpected error. Please try again.');
      }
    }
  });

  return (
    <Container maxWidth="xs" sx={{ pt: 8 }}>
      <Stack alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <BrandLogo size={48} />
        <Typography variant="h5">Ops Console</Typography>
        <Typography variant="body2" color="text.secondary">
          Sign in to continue.
        </Typography>
      </Stack>

      <Box component="form" onSubmit={onSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            label="Email"
            type="email"
            autoComplete="username"
            autoFocus
            error={Boolean(errors.email)}
            helperText={errors.email?.message}
            {...register('email')}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
            {...register('password')}
          />
          {serverError && <Alert severity="error">{serverError}</Alert>}
          <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </Button>
          <MuiLink component={RouterLink} to="/auth/password-reset" underline="hover">
            Forgot your password?
          </MuiLink>
        </Stack>
      </Box>
    </Container>
  );
}
